import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  CurrencyRupeeIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Badge, getStatusBadgeVariant, Modal, ModalFooter, ContentLoader, Select, BackButton, InsurerLogo } from '@/components/common';
import { policyNumberLabel } from '@/components/common/PolicyNumber';
import { claimExtrasRows } from './claimFormExtras';
import { ClaimDocumentsCard } from './ClaimDocumentsCard';
import { DocumentRequestsCard, InsurerClaimDesk } from './InsurerClaimDesk';
import { getErrorMessage } from '@/services';
import { claimsDeskService, type InsurerDeskClaim } from '@/services/claimsDesk';
// Imported by path rather than through components/common/index.ts, which is
// outside this task's file domain.
import { RecordTimestamps } from '@/components/common/RecordTimestamps';
import { useClaim, useClaimTimeline, useClaimStatusTransitions } from '@/hooks/useClaims';
// Task 10. `useUpdateClaimStatus` from '@/hooks/useClaims' is deliberately NOT
// used any more: its `claimService.updateStatus(id, status, notes)` cannot
// carry the description's visibility flag, and widening it means editing
// services/claims.ts and hooks/useClaims.ts, both outside this task's file
// domain. See the fold-back note on `claimNotesApi.updateStatus`.
import {
  APPROVAL_AMOUNT_STATUSES,
  PUBLISHED_REASON_STATUSES,
  SETTLEMENT_AMOUNT_STATUSES,
  isSharedWithCustomer,
  useAddClaimNote,
  useUpdateClaimStatusWithNote,
  type ClaimTimelineEntry,
} from './claimNotes';
import { useAuthStore } from '@/store';
import { isInsurerRole } from '@/routing/portalHome';
import { useCan } from '@/hooks/useEmployeeRights';
import { formatDate, formatDateTime, formatCurrency, titleCase } from '@/utils';

/**
 * Roles `core.permissions.base.IsBrokerEmployee` admits. Kept next to its only
 * use rather than exported: the authority is the server, and this list exists
 * only so the page does not offer a control the server will refuse.
 */
const BROKER_SIDE_ROLES = ['super_admin', 'country_admin', 'broker_admin', 'broker_employee'];

/**
 * B7: the happy-path claim lifecycle, used to render the progress tracker.
 *
 * This is a DISPLAY ordering only -- the authority on which transitions are
 * legal is `GET /claims/transitions/` (served from
 * `ClaimService.STATUS_TRANSITIONS`), and the API re-validates on every update.
 * Terminal branches (rejected / withdrawn) are deliberately absent: they end
 * the claim rather than sit on the path to settlement, and are shown as the
 * current step when reached.
 */
const CLAIM_PROGRESS_PATH = [
  'submitted',
  'acknowledged',
  'documents_pending',
  'under_review',
  'surveyor_assigned',
  'survey_completed',
  'assessment_pending',
  'approved',
  'settlement_pending',
  'settled',
  'closed',
];

export function ClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  // CUSTOMER-3: a customer must not change claim status. They keep a read-only
  // view of status via the Status card and the tracking timeline; the
  // "Update Status" control and its modal are hidden. The backend enforces the
  // same rule (ClaimViewSet.get_permissions) so this is not the only gate.
  const isCustomer = user?.role === 'customer';
  // #395/#396: insurer roles get a minimal read-only claim payload. The claim
  // documents and the tracking timeline are refused to them by the API (403),
  // so neither is requested nor drawn.
  const isInsurer = isInsurerRole(user?.role);
  // B4: document review, notes and status changes are claims Edit on the API
  // (`ModuleRight("claims")`), on top of the role floors below.
  const canEditClaims = useCan('claims', 'edit');
  // #215 "visibility for authorised broker/admin/claims users". These mirror the
  // backend gates rather than inventing a second rule: `verify`/`reject` on
  // ClaimDocumentViewSet carry `IsBrokerEmployee`, while the route itself is
  // `IsBrokerEmployee | IsCustomer` -- so a POSP can read the claim but gets a
  // 403 on either, and is shown neither control.
  const canReviewDocuments = BROKER_SIDE_ROLES.includes(user?.role ?? '') && canEditClaims;
  const canUploadDocuments = canReviewDocuments || isCustomer;
  // Task 10: the running commentary is the CLAIMS DESK talking, so the composer
  // follows the same role list the backend gate uses (`add_note` sits with
  // `update_status` in ClaimViewSet.get_permissions). A POSP can read the claim
  // but would get a 403 posting a note, so they are not shown the box.
  const canAddNotes = BROKER_SIDE_ROLES.includes(user?.role ?? '') && canEditClaims;
  // B4: `update_status` is the same gate as notes on the API -- a broker-staff
  // role floor (`IsBrokerEmployee`) plus claims Edit. Hiding it only from
  // customers let a POSP, an insurer user, or an employee whose claims Edit was
  // denied fill in the whole modal and then get a 403.
  const canUpdateStatus = BROKER_SIDE_ROLES.includes(user?.role ?? '') && canEditClaims;
  // #445: approval does not show a claim to the insurer; the broker admin sends it.
  const queryClient = useQueryClient();
  const canSendToInsurer = user?.role === 'broker_admin';
  const sendMutation = useMutation({
    mutationFn: () => claimsDeskService.sendToInsurer(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim', id] });
      queryClient.invalidateQueries({ queryKey: ['claim-timeline', id] });
      toast.success('Claim sent to the insurer');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [statusNoteShared, setStatusNoteShared] = useState(false);
  // B7: the Approved and Settled amounts are captured ON THE TRANSITION THAT
  // DECIDES THEM, which is why they live in the status modal and not in an
  // "edit claim" form. Nothing on any screen ever set either one, so the
  // Financial Details card below -- which has rendered all three since it was
  // built -- showed a dash forever.
  const [approvedAmount, setApprovedAmount] = useState('');
  const [settledAmount, setSettledAmount] = useState('');
  const [note, setNote] = useState('');
  const [noteShared, setNoteShared] = useState(false);

  const { data: claim, isLoading, error } = useClaim(id!);
  const { data: timeline } = useClaimTimeline(id!, !isInsurer);
  // #401: what has been asked for on this claim (the insurer desk draws its
  // own copy). The broker desk and the customer read it; a POSP is refused.
  const canReadDocumentRequests = !isInsurer && (isCustomer || BROKER_SIDE_ROLES.includes(user?.role ?? ''));
  const { data: documentRequests } = useQuery({
    queryKey: ['claim-document-requests', id],
    queryFn: () => claimsDeskService.documentRequests(id!),
    enabled: !!id && canReadDocumentRequests,
  });
  // `types/index.ts`'s `ClaimTimeline` is out of this task's file domain and is
  // wrong twice over: it has no `is_customer_visible`, and it declares
  // `old_status`/`new_status` as required fields the serializer has never sent.
  // `ClaimTimelineEntry` in ./claimNotes describes what the API really returns.
  const entries = (timeline ?? []) as unknown as ClaimTimelineEntry[];
  // B7: only the transitions that are legal from the claim's current status.
  const { data: statusTransitions } = useClaimStatusTransitions();
  const nextStatusOptions = statusTransitions?.[claim?.status ?? ''] ?? [];
  const isTerminalStatus = !!claim && !!statusTransitions && nextStatusOptions.length === 0;
  const updateStatusMutation = useUpdateClaimStatusWithNote(id!);
  const addNoteMutation = useAddClaimNote(id!);

  // The server publishes the reason for these transitions onto the claim record
  // itself (`rejected` -> `claim.rejection_reason`, which ClaimDetailSerializer
  // serves), so the claimant reads it whatever the checkbox says. Ticking and
  // locking the box is the honest thing to show; letting a broker believe a
  // rejection reason is private would be the UI telling a lie.
  const statusNoteIsForcedPublic = PUBLISHED_REASON_STATUSES.includes(newStatus);
  const statusNoteWillBeShared = statusNoteIsForcedPublic || statusNoteShared;

  // B7. Which amount this transition takes, if any — the same two lists the
  // server validates against, imported rather than restated.
  const needsApprovedAmount = APPROVAL_AMOUNT_STATUSES.includes(newStatus);
  const needsSettledAmount = SETTLEMENT_AMOUNT_STATUSES.includes(newStatus);
  const amountForThisStep = needsApprovedAmount ? approvedAmount : needsSettledAmount ? settledAmount : '';
  const toAmount = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  // The ceiling this step is measured against.
  //
  // APPROVAL: what was claimed. `null` there means the reference is unknown
  // (claimed_amount is nullable and many live claims carry none) and an unknown
  // reference is NOT a ceiling — the server takes the same view, so the button
  // must not disable on it.
  //
  // SETTLEMENT: what was approved, FALLING BACK to what was claimed. A missing
  // approved figure used to read as "no limit" on both sides, which meant every
  // claim raised before B7 — all of them carry approved_amount NULL — could be
  // settled for any amount at all. With neither figure on record the server
  // REFUSES the settlement outright, so the modal says so here rather than
  // letting the broker type a number into a 400.
  const settlementCeiling = needsSettledAmount
    ? (toAmount(claim?.approved_amount) ?? toAmount(claim?.claimed_amount))
    : null;
  const settlementCeilingLabel = toAmount(claim?.approved_amount) !== null ? 'approved' : 'claimed';
  const amountCeiling = needsApprovedAmount ? toAmount(claim?.claimed_amount) : settlementCeiling;
  const amountError = (() => {
    if (!needsApprovedAmount && !needsSettledAmount) return '';
    if (needsSettledAmount && settlementCeiling === null) {
      return 'This claim has no approved or claimed amount on record, so it cannot be settled. Record the approved amount first.';
    }
    if (!amountForThisStep) return '';
    const parsed = Number(amountForThisStep);
    if (!Number.isFinite(parsed)) return 'Enter the amount as a number.';
    if (parsed < 0) return 'The amount cannot be negative.';
    if (amountCeiling !== null && parsed > amountCeiling) {
      return needsApprovedAmount
        ? `Cannot approve more than the ${formatCurrency(amountCeiling)} claimed.`
        : `Cannot settle more than the ${formatCurrency(amountCeiling)} ${settlementCeilingLabel}.`;
    }
    return '';
  })();
  // These mirror the server's rules so the broker is told before they submit,
  // NOT so the check happens here: `ClaimStatusUpdateSerializer` re-validates
  // every one of them and its 400 is surfaced by the mutation's toast.
  const amountIsBlocking =
    ((needsApprovedAmount || needsSettledAmount) && !amountForThisStep) || !!amountError;

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setNewStatus('');
    setStatusNote('');
    setStatusNoteShared(false);
    setApprovedAmount('');
    setSettledAmount('');
  };

  const handleStatusUpdate = () => {
    if (!newStatus || amountIsBlocking) return;
    updateStatusMutation.mutate(
      {
        status: newStatus,
        note: statusNote.trim(),
        isCustomerVisible: statusNoteWillBeShared,
        // Passed only for the step that takes it; `claimNotesApi.updateStatus`
        // drops the other, because the server refuses an amount on a transition
        // that does not take one rather than silently ignoring it.
        approvedAmount: needsApprovedAmount ? approvedAmount : undefined,
        settledAmount: needsSettledAmount ? settledAmount : undefined,
      },
      { onSuccess: closeStatusModal }
    );
  };

  // Task 10: a note WITHOUT a stage change. Several of these within one stage is
  // the normal case, so the composer stays where it is and only clears itself.
  const handleAddNote = () => {
    const text = note.trim();
    if (!text) return;
    addNoteMutation.mutate(
      { note: text, isCustomerVisible: noteShared },
      {
        onSuccess: () => {
          setNote('');
          setNoteShared(false);
        },
      }
    );
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTimelineDotColor = (action: string, newStatus: string | null) => {
    const status = newStatus || action;
    switch (status) {
      case 'settled':
      case 'approved':
      case 'partially_approved':
        return 'bg-green-500';
      case 'rejected':
      case 'closed':
        return 'bg-red-500';
      default:
        return 'bg-blue-500';
    }
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !claim) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Claim not found</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">The claim you are looking for does not exist.</p>
        <Link to="/claims" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Claims
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/claims" />
      </div>
      <PageHeader
        title={claim.claim_number}
        subtitle={`Policy: ${policyNumberLabel(claim)}${
          claim.forwarded_to_insurer_at ? ` · Sent to insurer ${formatDateTime(claim.forwarded_to_insurer_at)}` : ''
        }`}
        breadcrumbs={[
          { label: 'Claims', href: '/claims' },
          { label: claim.claim_number },
        ]}
        actions={
          !canUpdateStatus && !(canSendToInsurer && claim.status === 'approved') ? undefined : (
            <div className="flex items-center gap-3">
              {canSendToInsurer && claim.status === 'approved' && !claim.forwarded_to_insurer_at && (
                <Button onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending}>
                  Send to insurer
                </Button>
              )}
              {canUpdateStatus && (
                <>
                  {/* #633: the edit form (description, hospital, extras). */}
                  <Link to={`/claims/${claim.id}/edit`}>
                    <Button variant="outline">Edit</Button>
                  </Link>
                  <Button variant="outline" onClick={() => setShowStatusModal(true)}>
                    Update Status
                  </Button>
                </>
              )}
            </div>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* B7: the step-by-step workflow, so the broker can see where the
              claim sits and what is pending rather than inferring it from a
              status badge. */}
          <Card>
            <CardHeader title="Claim Progress" />
            <ClaimProgressTracker status={claim.status} />
          </Card>

          {/* #401: the insurer's Claims Desk -- actions, documents, document
              requests and the curated history (insurer roles never get the
              raw timeline). */}
          {isInsurer && <InsurerClaimDesk claim={claim as InsurerDeskClaim} />}

          {/* Where the claim has BEEN, directly under where it IS. These two
              answer the customer's question together ("what is happening with
              my claim") and used to be separated by three cards of reference
              data, with the history last on the page. */}
          {!isInsurer && (
            <Card>
              <CardHeader title="Claim Tracking" subtitle="Status history and notes" />
              {canAddNotes && (
                <ClaimNoteComposer
                  note={note}
                  onNoteChange={setNote}
                  shared={noteShared}
                  onSharedChange={setNoteShared}
                  onSubmit={handleAddNote}
                  isPending={addNoteMutation.isPending}
                />
              )}
              {entries.length > 0 ? (
                <div className="relative">
                  {entries.map((entry, index) => {
                    const isLast = index === entries.length - 1;
                    const dotColor = getTimelineDotColor(entry.action, entry.new_status ?? null);
                    const isNote = entry.action === 'note_added';
                    return (
                      <div key={entry.id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full mt-1 ring-4 ring-white ${dotColor}`} />
                          {!isLast && <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700" />}
                        </div>
                        <div className={`flex-1 ${isLast ? '' : 'pb-6'}`}>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{titleCase(entry.action)}</p>
                            {entry.new_status && (
                              <Badge variant={getStatusBadgeVariant(entry.new_status)} size="sm">
                                {titleCase(entry.new_status)}
                              </Badge>
                            )}
                          </div>
                          {entry.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{entry.description}</p>
                          )}
                          {/* Task 10: once the modal has closed, a shared note and
                              an internal one look identical -- which is exactly
                              how an internal remark gets written as if the
                              customer were reading it. Only notes are marked:
                              lifecycle rows are always the claimant's history and
                              a badge on every one of them would be noise. */}
                          {isNote && (
                            <Badge
                              variant={isSharedWithCustomer(entry) ? 'info' : 'default'}
                              size="sm"
                              className="mt-1"
                            >
                              {isSharedWithCustomer(entry) ? 'Shared with customer' : 'Internal'}
                            </Badge>
                          )}
                          <div className="flex items-center gap-3 mt-1">
                            <div className="flex items-center gap-1">
                              <ClockIcon className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-400">{formatDateTime(entry.created_at)}</span>
                            </div>
                            {entry.performed_by_name && (
                              <div className="flex items-center gap-1">
                                <UserIcon className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-400">{entry.performed_by_name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No tracking history available.</p>
              )}
            </Card>
          )}

          {/* Claim Details */}
          <Card>
            <CardHeader title="Claim Information" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InfoItem label="Claim Type" value={claim.claim_type_display || titleCase(claim.claim_type)} />
              <InfoItem label="Customer" value={claim.customer_name} />
              <InfoItem label="Broker" value={claim.broker_name} />
              <InfoItem label="Incident Date" value={formatDate(claim.incident_date)} />
              {/* U-05: whose policy this claim is against, with the insurer's
                  logo and, when it has one, its blurb. */}
              {claim.insurer_name && (
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Insurer</p>
                  <span className="mt-0.5 flex items-center gap-2">
                    <InsurerLogo
                      src={claim.insurer_logo_url}
                      name={claim.insurer_name}
                      description={claim.insurer_description}
                      size="xs"
                    />
                    <span className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-white">
                      {claim.insurer_name}
                    </span>
                  </span>
                  {claim.insurer_description && (
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{claim.insurer_description}</p>
                  )}
                </div>
              )}
            </div>
            <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Incident Description</p>
              <p className="text-sm text-gray-900 dark:text-white">{claim.incident_description}</p>
            </div>
            {claim.incident_location && (
              <div className="mt-4 flex items-center gap-2">
                <MapPinIcon className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">{claim.incident_location}</span>
              </div>
            )}
            {/* DQ-453: the claim form's FIR status, vehicle location and third-party loss. */}
            {claimExtrasRows(claim.claim_data).length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {claimExtrasRows(claim.claim_data).map((row) => (
                  <InfoItem key={row.label} label={row.label} value={row.value} />
                ))}
              </div>
            )}
            {/* DQ-346: the hospital chosen on a health claim. */}
            {(claim.network_hospital_detail || claim.hospital_name) && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Hospital</p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {claim.network_hospital_detail?.name || claim.hospital_name}
                  {claim.network_hospital_detail && (
                    <span className="ml-2 text-xs text-green-600 dark:text-green-400">Network hospital</span>
                  )}
                </p>
                {claim.network_hospital_detail && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {[
                      claim.network_hospital_detail.address,
                      claim.network_hospital_detail.city_name,
                      claim.network_hospital_detail.pincode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Financial Details */}
          <Card>
            <CardHeader title="Financial Details" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CurrencyRupeeIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">Claimed Amount</span>
                </div>
                {/* B7: `claimed_amount` IS NULLABLE, and this was the one of
                    the three tiles without a null guard -- `formatCurrency`
                    runs parseFloat over it, so a claim with no figure rendered
                    the literal string "₹NaN". Every claim raised before this
                    batch is such a claim, because no form ever sent an amount. */}
                <p className="text-xl font-semibold text-gray-900 dark:text-white">
                  {claim.claimed_amount ? formatCurrency(claim.claimed_amount) : '-'}
                </p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CurrencyRupeeIcon className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600 dark:text-green-400">Approved Amount</span>
                </div>
                <p className="text-xl font-semibold text-green-700 dark:text-green-300">
                  {claim.approved_amount ? formatCurrency(claim.approved_amount) : '-'}
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CurrencyRupeeIcon className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-blue-600 dark:text-blue-400">Settled Amount</span>
                </div>
                <p className="text-xl font-semibold text-blue-700 dark:text-blue-300">
                  {claim.settled_amount ? formatCurrency(claim.settled_amount) : '-'}
                </p>
              </div>
            </div>
          </Card>

          {/* Surveyor Information */}
          {claim.surveyor && (
            <Card>
              <CardHeader title="Surveyor Information" />
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{claim.surveyor_name}</p>
                  {claim.surveyor_assigned_at && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Assigned: {formatDateTime(claim.surveyor_assigned_at)}
                    </p>
                  )}
                  {claim.survey_completed_at && (
                    <p className="text-sm text-green-600 dark:text-green-400">
                      Completed: {formatDateTime(claim.survey_completed_at)}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Documents — board #215. Upload, the required-document checklist,
              download, verify/reject and delete now live in ClaimDocumentsCard,
              which talks to the shipped backend. What was here before was a
              read-only list that never rendered (it called `.length` on the
              paginated envelope) and whose "View" link pointed at `file_path`,
              a storage path rather than a URL. */}
          {canReadDocumentRequests && (
            <DocumentRequestsCard requests={documentRequests ?? []} claimId={id!} />
          )}

          {!isInsurer && (
            <ClaimDocumentsCard
              claimId={id!}
              claimType={claim.claim_type}
              claimStatus={claim.status}
              canReview={canReviewDocuments}
              canUpload={canUploadDocuments}
            />
          )}

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Current Status</span>
                <Badge variant={getStatusBadgeVariant(claim.status)}>
                  {titleCase(claim.status)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Priority</span>
                <Badge variant={getPriorityBadgeVariant(claim.priority)}>
                  {titleCase(claim.priority)}
                </Badge>
              </div>
            </div>
          </Card>

          {/* The sidebar used to carry two more views of this same data: an
              "Activity Timeline" showing the first five entries of the history
              that now leads the main column, and a SECOND card also titled
              "Claim Tracking" — a hand-rolled five-step copy of the Claim
              Progress tracker, with its own private status order that had to
              be kept in step with the real one by hand. Two cards sharing a
              title while showing different things was the reported defect;
              three renderings of one status is why it survived. Both removed.
              Claim Progress and Claim Tracking above are now the only two. */}

          {/* Quick Actions */}
          <Card>
            <CardHeader title="Quick Actions" />
            <div className="space-y-3">
              <Link to={`/policies/${claim.policy}`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  View Policy
                </Button>
              </Link>
              {claim.customer && (
                <Link to={`/customers/${claim.customer}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    View Customer
                  </Button>
                </Link>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* A6: record audit footer, below both columns. */}
      <RecordTimestamps createdAt={claim.created_at} updatedAt={claim.updated_at} />

      {/* Update Status Modal — broker staff with claims Edit only (CUSTOMER-3, B4) */}
      {canUpdateStatus && (
        <Modal isOpen={showStatusModal} onClose={closeStatusModal} title="Update Claim Status">
          <div className="space-y-4">
            {/* B7: was a flat list of every status -- one of which
                (`under_investigation`) is not even a valid STATUS_CHOICES
                value, so picking it always failed. Now only the legal next
                steps from the claim's current status are offered. */}
            {isTerminalStatus ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This claim is {titleCase(claim.status)} and has no further steps.
              </p>
            ) : (
              <>
                <Select
                  // `Select` derives its id from `id || props.name`, and this
                  // one is controlled so it has neither -- the <label> had no
                  // `htmlFor` and pointed at nothing, for a screen reader as
                  // much as for a test. Same fix as "Incident Day" on the form.
                  id="claim-next-status"
                  label="Next Status"
                  options={[{ value: '', label: 'Select the next step' }, ...nextStatusOptions]}
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                />
                {/* B7: the amount this step decides, asked for on the step that
                    decides it. Approved on `approved`/`partially_approved` (the
                    second path into settlement), Settled on `settled` -- and on
                    `settled` only, because the graph is
                    approved -> settlement_pending -> settled. */}
                {(needsApprovedAmount || needsSettledAmount) && (
                  <div>
                    <label
                      htmlFor="claim-status-amount"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                    >
                      {needsApprovedAmount ? 'Approved Amount' : 'Settled Amount'}
                    </label>
                    <input
                      id="claim-status-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      value={amountForThisStep}
                      onChange={(e) =>
                        needsApprovedAmount
                          ? setApprovedAmount(e.target.value)
                          : setSettledAmount(e.target.value)
                      }
                      placeholder="Amount in rupees"
                    />
                    {amountError ? (
                      <p className="mt-1 text-sm text-red-600">{amountError}</p>
                    ) : (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {needsApprovedAmount
                          ? amountCeiling !== null
                            ? `Required. ${formatCurrency(amountCeiling)} was claimed.`
                            : 'Required. No Claimed Amount was recorded on this claim.'
                          : amountCeiling !== null
                            ? `Required. ${formatCurrency(amountCeiling)} was approved.`
                            : 'Required. No Approved Amount was recorded on this claim.'}
                      </p>
                    )}
                  </div>
                )}
                {/* Task 10: the modal used to contain this Select and nothing
                    else, so a stage change landed on the timeline as a bare
                    "Status changed from X to Y" and the contact was told the
                    name of a state and nothing more. `update_status` has
                    accepted `notes` all along -- the UI simply never sent one. */}
                <div>
                  <label
                    htmlFor="claim-status-note"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                  >
                    What changed?
                  </label>
                  <textarea
                    id="claim-status-note"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    rows={3}
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder="Why is the claim moving? This goes on the tracking timeline."
                  />
                </div>
                <ShareWithCustomerCheckbox
                  id="claim-status-note-shared"
                  label="Share this description with the customer"
                  checked={statusNoteWillBeShared}
                  disabled={statusNoteIsForcedPublic}
                  onChange={setStatusNoteShared}
                  hint={
                    statusNoteIsForcedPublic
                      ? 'A rejection reason is stored on the claim and is always shown to the customer.'
                      : undefined
                  }
                />
              </>
            )}
          </div>
          <ModalFooter>
            <Button variant="outline" onClick={closeStatusModal}>
              Cancel
            </Button>
            {/* "Save Status", not "Update Status": the page header already has
                an "Update Status" button, and two buttons with one accessible
                name is ambiguous to a screen reader and to a test. */}
            <Button
              onClick={handleStatusUpdate}
              isLoading={updateStatusMutation.isPending}
              disabled={!newStatus || amountIsBlocking}
            >
              Save Status
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

/**
 * Task 10: the "share with the customer" opt-in, used by both free-text boxes.
 *
 * `id` is passed explicitly and the label bound with `htmlFor` because most
 * inputs in this codebase have neither, which is why label-based queries
 * usually match nothing here. The label carries NO required marker: a `*`
 * inside the label text breaks `getByLabelText` matching.
 */
function ShareWithCustomerCheckbox({
  id,
  label,
  checked,
  disabled,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  hint?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500 disabled:opacity-60"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label htmlFor={id} className="text-sm text-gray-700 dark:text-gray-200">
          {label}
        </label>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        {hint ?? 'Leave this off to keep the remark internal to your team.'}
      </p>
    </div>
  );
}

/**
 * Task 10: add a note to the claim WITHOUT changing its stage.
 *
 * This is the half that did not exist at all. `note_added` has been in
 * `ClaimActivityLog.ACTION_CHOICES` since the model was written and the claims
 * app never wrote one, so the only way to say anything about a claim was to
 * move it -- a claim parked in "Documents Pending" for a fortnight had a blank
 * commentary by construction. The composer stays open after a successful post
 * because several notes within one stage is the normal case.
 */
function ClaimNoteComposer({
  note,
  onNoteChange,
  shared,
  onSharedChange,
  onSubmit,
  isPending,
}: {
  note: string;
  onNoteChange: (next: string) => void;
  shared: boolean;
  onSharedChange: (next: boolean) => void;
  onSubmit: () => void;
  isPending: boolean;
}) {
  return (
    <div className="mb-6 pb-6 border-b border-gray-100 dark:border-gray-700 space-y-3">
      <div>
        <label
          htmlFor="claim-note"
          className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
        >
          Note
        </label>
        <textarea
          id="claim-note"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          rows={2}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="What has happened since the last update?"
        />
      </div>
      <ShareWithCustomerCheckbox
        id="claim-note-shared"
        label="Share this note with the customer"
        checked={shared}
        onChange={onSharedChange}
      />
      <div className="flex justify-end">
        <Button onClick={onSubmit} isLoading={isPending} disabled={!note.trim()}>
          Add Note
        </Button>
      </div>
    </div>
  );
}

/**
 * B7: renders the claim's position along the lifecycle -- completed, current
 * and still-pending steps.
 *
 * A status that is not on the happy path (rejected, withdrawn, or the
 * partially_approved branch) is shown on its own as the current step, rather
 * than being forced into a sequence it does not belong to.
 */
function ClaimProgressTracker({ status }: { status: string }) {
  const currentIndex = CLAIM_PROGRESS_PATH.indexOf(status);

  if (currentIndex === -1) {
    return (
      <div className="flex items-center gap-3">
        <span className="h-3 w-3 rounded-full bg-primary-600 shrink-0" />
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{titleCase(status)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This claim has left the standard settlement path.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ol className="space-y-0">
      {CLAIM_PROGRESS_PATH.map((step, index) => {
        const isDone = index < currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <li key={step} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={`h-3 w-3 rounded-full shrink-0 mt-1.5 ${
                  isDone ? 'bg-green-500' : isCurrent ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              />
              {index < CLAIM_PROGRESS_PATH.length - 1 && (
                <span className={`w-px flex-1 ${isDone ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
              )}
            </div>
            <p
              className={`pb-4 text-sm ${
                isCurrent
                  ? 'font-semibold text-gray-900 dark:text-white'
                  : isDone
                    ? 'text-gray-600 dark:text-gray-300'
                    : 'text-gray-400'
              }`}
            >
              {titleCase(step)}
              {isCurrent && <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">current</span>}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">{value}</p>
    </div>
  );
}
